import { useContext } from 'react';

import type { ReportGridRow } from 'src/components/reports/types';
import { Apollo } from 'src/gql/generated';
import { useRelationshipActions } from 'src/hooks';

import {
  DocumentGridDisplayModeContext,
  DocumentGridMissingMatchesOnlyContext,
  DocumentGridReportIdContext,
  DocumentGridUnconfirmedOnlyContext,
} from './RepositoryContextProviders';
import { useRelationship } from './repositoryDocumentNameCellHelpers';

type DocWithRelationshipTimestamps = Pick<
  Apollo.Document,
  | 'relationshipMatchingStartedAt'
  | 'relationshipMatchingFinishedAt'
  | 'relationshipMatchingFailedReason'
>;

export function getIsRelationshipMatchingRunning(
  doc: DocWithRelationshipTimestamps | null
): boolean {
  return Boolean(
    doc?.relationshipMatchingStartedAt &&
    !doc.relationshipMatchingFinishedAt &&
    !doc.relationshipMatchingFailedReason
  );
}

export function getIsRelationshipLookingForAMatch(
  doc: DocWithRelationshipTimestamps,
  relationship: Pick<Apollo.DocumentRelationship, 'childDocumentId' | 'parentDocumentId'>
): boolean {
  const isMatchFound = Boolean(relationship.childDocumentId && relationship.parentDocumentId);
  return getIsRelationshipMatchingRunning(doc) && !isMatchFound;
}

interface UseRelationshipNameCellStateOptions {
  row: ReportGridRow;
  value: string | null;
}

export function useRelationshipNameCellState({ row, value }: UseRelationshipNameCellStateOptions) {
  const { relationship } = useRelationship(row);
  const isRootLevelDocument = !row.relationshipDirection;
  const displayMode = useContext(DocumentGridDisplayModeContext);
  const repositoryId = useContext(DocumentGridReportIdContext);
  const unconfirmedRelationshipsOnly = useContext(DocumentGridUnconfirmedOnlyContext);
  const missingMatchesOnly = useContext(DocumentGridMissingMatchesOnlyContext);
  const isChildDocument = row.relationshipDirection === Apollo.RelationshipDirection.Child;
  const isParentDocument = row.relationshipDirection === Apollo.RelationshipDirection.Parent;
  const actions = useRelationshipActions({
    repositoryId,
    row,
  });
  const showRelationshipLabel =
    Boolean(relationship?.childRelationshipType) && (isChildDocument || isParentDocument);
  const showRelationshipActions = showRelationshipLabel && relationship?.review?.outcome === null;
  const isRelationshipExtractionInProgress = getIsRelationshipMatchingRunning(row);
  const showRelatedCount =
    displayMode === 'relationship' &&
    isRootLevelDocument &&
    (row.childRowCount > 0 || isRelationshipExtractionInProgress);
  const isRelationShipMatchingRunning =
    !isRootLevelDocument && displayMode === 'relationship' && isRelationshipExtractionInProgress;
  const isMatched = Boolean(relationship?.childDocumentId && relationship?.parentDocumentId);
  const isLookingFoMatch = isRelationShipMatchingRunning && !isMatched;
  const displayedDocumentName = isLookingFoMatch
    ? 'Matching document...'
    : value || 'Untitled document';

  return {
    actions,
    displayedDocumentName,
    isLookingFoMatch,
    isParentDocument,
    isRelationshipExtractionInProgress,
    missingMatchesOnly,
    relationship,
    showRelatedCount,
    showRelationshipActions,
    showRelationshipLabel,
    unconfirmedRelationshipsOnly,
  };
}
